import {
    Box,
    Input,
    Button,
    Checkbox,
    Flex,
    RadioGroup,
    Radio,
    Text as PlasticText,
    useBrand
} from '@cof/plastic-components';
import { useFormContext } from 'react-hook-form';
import { useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { withText } from '../../../text/textStore';
import PhoneNumber from './PhoneNumber';
import { autoCompleteMapping } from '../../../utilities/constants';
import { styled, css } from 'styled-components';
import { FullInputWidth, InputWrapper } from './SharedStyledComponents';
import ErrorHandler, { shouldDisplayFormTick, shouldDisplayRadioTick } from './ErrorHandler';
import { LabelSection } from '../../../components/TextField/TextField';
import { FORM_SECTIONS } from './FormIDs';
import { FormUtilsContext } from '../QuickCheck';
import Group from '../../../components/Group';
import { useFormData } from '../../../components/FormDataContext';
import BalanceTransfer from './BalanceTransfer';
import Label from '../../../components/Label/Label';
import useQueryParams from '../../../utilities/useQueryParams';
import { useLandmarkRefsContext } from '../../../components/LandmarkRefsProvider';
import { getAriaDescribedBy, isPartnerOrAggsEdit } from '../../../utilities/utils';
import { useAggregatorConsumerName } from '../../../utilities/aggregatorConsumerNameProvider';
import DateOfBirthInput from './DateOfBirthInput';
import { HiddenLegend } from '../../../components/PhoneNumber/PhoneNumber';

const BoxRow = styled(Flex)`
    display: flex;
    gap: 1.2rem;
`;

const HalfBoxWidth = styled(Box)`
    flex: 1;

    & input {
        width: 100%;
        box-sizing: border-box;
    }
`;

const ButtonPositioner = styled(Flex)`
    ${({ theme }: any) => css`
        margin-top: ${theme.space.lg};
        font-size: ${theme.fontSizes.textMediumMobile};
        ${theme.breakpoints.mq.tabletMin} {
            font-size: ${theme.fontSizes.textSmallDesktop};
        }
    `};
    justify-content: center;
    align-items: center;
`;

const titles = ['Mr', 'Mrs', 'Miss', 'Ms', 'Dr'];

const getMarketingDefaultValue = (isEdit: any, formData: any) => {
    const prePopValue = formData?.formValues?.[FORM_SECTIONS.Permissions.Marketing] ?? true; // need to default this to true for if the quotation is invalid
    return isEdit ? prePopValue.toString() === 'true' : true;
};

const TitleAndNameForm = ({ Text }: any) => {
    const {
        register,
        formState: { errors, touchedFields },
        getValues,
        watch,
        setValue
    } = useFormContext();
    const { isBT, isEdit, formData } = useFormData();
    const [aggregatorConsumerName] = useAggregatorConsumerName();
    const brand = useBrand();
    // @ts-expect-error TS2339: Property 'variantTernary' does not exist on type 'null'.
    const { variantTernary } = useContext(FormUtilsContext);
    const aboutYouIDs = FORM_SECTIONS.AboutYou;
    const [showHomeNo, setShowHomeNo] = useState(isEdit && !!formData?.formValues?.[aboutYouIDs.HomeNumber]);
    const buttonHandler = () => {
        setShowHomeNo(true);
    };

    const queryParams = useQueryParams();

    // @ts-expect-error TS2339: Property 'submitAttempted' does not exist on type ''.
    const { submitAttempted } = useLandmarkRefsContext();
    useEffect(() => {
        if (queryParams.firstName) setValue(aboutYouIDs.FirstName, queryParams.firstName);
        if (queryParams.lastName) setValue(aboutYouIDs.LastName, queryParams.lastName);
    }, [queryParams.firstName, queryParams.lastName]);

    return (
        <>
            <Box>
                {isBT && <BalanceTransfer />}
                <Flex>
                    <RadioGroup
                        // @ts-expect-error TS2322: Property 'id' does not exist on the RadioGroup props type.
                        id={aboutYouIDs.Title}
                        name={aboutYouIDs.Title}
                        variant="square"
                        aria-describedby={getAriaDescribedBy(aboutYouIDs.Title, submitAttempted, [], errors)}
                    >
                        <LabelSection
                            id={aboutYouIDs.Title}
                            label={<Text path="Title.Heading"></Text>}
                            mt="xs"
                            py="xs"
                            // @ts-expect-error TS2554: Expected 5 arguments, but got 3.
                            displayTick={shouldDisplayRadioTick(aboutYouIDs.Title, watch, errors)}
                            as="legend"
                            data-qm-allow
                        />{' '}
                        {titles.map((title) => (
                            <Radio
                                key={`title-button-${title}`}
                                value={title}
                                {...register(aboutYouIDs.Title, { required: 'Please select an option for title' })}
                                autoComplete={autoCompleteMapping['title']}
                                data-qm-allow
                            >
                                {title}
                            </Radio>
                        ))}
                    </RadioGroup>
                </Flex>
                <ErrorHandler id={aboutYouIDs.Title} show={touchedFields[aboutYouIDs.Title]} data-qm-allow />

                <InputWrapper>
                    <BoxRow>
                        <HalfBoxWidth>
                            <Group>
                                <LabelSection
                                    id={aboutYouIDs.FirstName}
                                    label={<Text path="FirstName"></Text>}
                                    displayTick={shouldDisplayFormTick(aboutYouIDs.FirstName, getValues, errors)}
                                    data-qm-allow
                                />
                                <Input
                                    id={aboutYouIDs.FirstName}
                                    data-qa-id={aboutYouIDs.FirstName}
                                    {...register(aboutYouIDs.FirstName, {
                                        required: 'First name is required',
                                        maxLength: {
                                            value: 32,
                                            message: 'Your first name must be 32 characters or less'
                                        },
                                        minLength: {
                                            value: 2,
                                            message: 'Your first name must be longer than one character'
                                        },
                                        pattern: {
                                            value: /^[a-zA-Z'`,. -]+$/,
                                            message: 'This is not a valid first name'
                                        }
                                    })}
                                    variant={variantTernary(aboutYouIDs.FirstName)}
                                    autoComplete={autoCompleteMapping['first-name']}
                                    aria-describedby={getAriaDescribedBy(
                                        aboutYouIDs.FirstName,
                                        submitAttempted,
                                        [],
                                        errors
                                    )}
                                />
                                <ErrorHandler
                                    id={aboutYouIDs.FirstName}
                                    show={touchedFields[aboutYouIDs.FirstName]}
                                    data-qm-allow
                                />
                            </Group>
                        </HalfBoxWidth>

                        <HalfBoxWidth>
                            <Group>
                                <LabelSection
                                    id={aboutYouIDs.LastName}
                                    label={<Text path="LastName"></Text>}
                                    displayTick={shouldDisplayFormTick(aboutYouIDs.LastName, getValues, errors)}
                                    data-qm-allow
                                />

                                <Input
                                    id={aboutYouIDs.LastName}
                                    data-qa-id={aboutYouIDs.LastName}
                                    {...register(aboutYouIDs.LastName, {
                                        required: 'Last name is required',
                                        maxLength: {
                                            value: 32,
                                            message: 'Your last name must be 32 characters or less'
                                        },
                                        minLength: {
                                            value: 2,
                                            message: 'Your last name must be longer than one character'
                                        },
                                        pattern: {
                                            value: /^[a-zA-Z'`,. -]+$/,
                                            message: 'This is not a valid last name'
                                        }
                                    })}
                                    variant={variantTernary(aboutYouIDs.LastName)}
                                    autoComplete={autoCompleteMapping['last-name']}
                                    aria-describedby={getAriaDescribedBy(
                                        aboutYouIDs.LastName,
                                        submitAttempted,
                                        [],
                                        errors
                                    )}
                                />
                                <ErrorHandler
                                    id={aboutYouIDs.LastName}
                                    show={touchedFields[aboutYouIDs.LastName]}
                                    data-qm-allow
                                />
                            </Group>
                        </HalfBoxWidth>
                    </BoxRow>
                </InputWrapper>

                <DateOfBirthInput />

                <Group>
                    <InputWrapper>
                        <LabelSection
                            id={aboutYouIDs.EmailAddress}
                            label={<Text path="EmailAddress"></Text>}
                            displayTick={shouldDisplayFormTick(aboutYouIDs.EmailAddress, getValues, errors)}
                            data-qm-allow
                        />

                        <FullInputWidth
                            id={aboutYouIDs.EmailAddress}
                            data-qa-id={aboutYouIDs.EmailAddress}
                            {...register(aboutYouIDs.EmailAddress, {
                                required: 'Email address is required',
                                pattern: {
                                    value: /^[a-zA-Z0-9_+*-]+(?:\.[a-zA-Z0-9_+*-]+)*@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.)|([a-zA-Z0-9-]+\.)+)([a-zA-Z]{2,63}|[0-9]{1,3})(\]?)$/,
                                    message: 'This is not a valid email address'
                                }
                            })}
                            variant={variantTernary(aboutYouIDs.EmailAddress)}
                            aria-describedby={getAriaDescribedBy(aboutYouIDs.EmailAddress, submitAttempted, [], errors)}
                            autoComplete={autoCompleteMapping['email-address']}
                        />
                        <ErrorHandler
                            id={aboutYouIDs.EmailAddress}
                            show={touchedFields[aboutYouIDs.EmailAddress]}
                            data-qm-allow
                        />
                    </InputWrapper>
                </Group>

                <Group as="fieldset">
                    <HiddenLegend aria-hidden="true">Phone numbers</HiddenLegend>
                    <InputWrapper data-qa-id={`${aboutYouIDs.MobileNumber}-input-wrapper`}>
                        <LabelSection
                            id={aboutYouIDs.MobileNumber}
                            label={<Text path="MobileNumber.Heading"></Text>}
                            htmlFor={aboutYouIDs.MobileNumber}
                            displayTick={shouldDisplayFormTick(aboutYouIDs.MobileNumber, getValues, errors)}
                            data-qm-allow
                        />

                        <PhoneNumber id={aboutYouIDs.MobileNumber} name="mobileNumber" />
                    </InputWrapper>

                    {showHomeNo ? (
                        <Group>
                            <InputWrapper>
                                <LabelSection
                                    id={aboutYouIDs.HomeNumber}
                                    label={<Text path="HomeNumber"></Text>}
                                    displayTick={shouldDisplayFormTick(aboutYouIDs.HomeNumber, getValues, errors)}
                                    data-qm-allow
                                />
                                <PhoneNumber id={aboutYouIDs.HomeNumber} name={'homeNumber'} />
                            </InputWrapper>
                        </Group>
                    ) : (
                        <ButtonPositioner pb="sm">
                            {/* textSize cannot be set using styled system so we handle this in the positioner and then inherit the size */}
                            <Button
                                variant="secondary"
                                onClick={buttonHandler}
                                // @ts-expect-error TS2322: Type 'string' is not assignable to type 'ValidButtonTextSizes | undefined'.
                                textSize={'inherit'}
                                data-qa-id="about-you:phone-numbers:home-number-link"
                                data-qm-allow
                            >
                                {<Text data-qm-allow path="AddHomeNumber"></Text>}
                            </Button>
                        </ButtonPositioner>
                    )}
                </Group>
                {!isPartnerOrAggsEdit(brand, aggregatorConsumerName) && (
                    <InputWrapper style={{ marginBottom: 0 }}>
                        <Label data-qm-allow id={FORM_SECTIONS.Permissions.Marketing}>
                            <Flex gap="xs">
                                <Checkbox
                                    // @ts-expect-error TS2783: 'name' is specified more than once, so this usage will be overwritten.
                                    name={FORM_SECTIONS.Permissions.Marketing}
                                    {...register(FORM_SECTIONS.Permissions.Marketing)}
                                    defaultChecked={getMarketingDefaultValue(isEdit, formData)}
                                />
                                <PlasticText marginTop={'-3px'}>
                                    <Text path="MarketingPermission"></Text>
                                </PlasticText>
                            </Flex>
                        </Label>
                    </InputWrapper>
                )}
            </Box>
        </>
    );
};
TitleAndNameForm.propTypes = {
    Text: PropTypes.func.isRequired
};

export default withText('AboutYou')(TitleAndNameForm);
